"use client"
import { memo, useMemo, useRef, useState } from "react"
import { useAnimatedDate } from "../../hooks/useAnimatedDate"
import { createMonotoneSmoothedScreenPoints, type PathPoint } from "../../extrapolated-graph/graphUtils"
import type { BranchKey, ComparisonPlanId, MetricAxis, MetricScenarioSeries, SeriesPoint } from "./metricDetailsTypes"

// Monochrome except the red default path, which uses the site's dark-red
// accent token — the same red as the graph's loss-of-control layer. (Not
// var(--accent): that is green and only flips to this red inside a `.race`
// branch — app/globals.css.)
const BLACK = "#111111"
const RED = "var(--race-background)"

// Post-fork line hierarchy (2026-07-09 redesign). Two dash vocabularies
// only: DASHES for the two protagonists — Plan A heavy/near-continuous in
// black, the default path mid-weight in red — and DOTS for everything faint
// (the B/C/S context lines share the dotted voice of the pre-fork shared
// projection). Context lines are hairline BLACK, not gray — de-emphasis by
// weight and texture alone, per the style guide's no-gray rule.
const PLAN_A_STROKE_WIDTH = 2
const PLAN_A_DASH = "7 3.5"
const DEFAULT_DASH = "4 3"
const CONTEXT_STROKE_WIDTH = 1
const CONTEXT_DASH = "0.1 3.4"

// ── Fixed drawing space ─────────────────────────────────────────────────────
// One uniformly-scaled SVG sized to the HUD column (455px wide → scale ≈ 1),
// labels drawn INSIDE it in the HUD's mono, so the chart is a single
// instrument face rather than an HTML/SVG label sandwich.
const W = 455
const H = 216
const PX0 = 36 // plot left (y labels live in the left gutter)
const PX1 = 404 // plot right (end labels live in the right gutter)
const PY0 = 8 // plot top
const PY1 = 202 // plot bottom (three bare year labels below)

const yForValueWith = (axis: MetricAxis, py1: number = PY1) => (value: number) => {
  if (axis.type === "log") {
    const lo = Math.log10(axis.min)
    const hi = Math.log10(axis.max)
    return PY0 + (1 - (Math.log10(value) - lo) / (hi - lo)) * (py1 - PY0)
  }
  return PY0 + (1 - value / (axis.max ?? 1)) * (py1 - PY0)
}

// Merge observed+projection+branch points, unique by year, sorted.
const uniqByYear = (pts: SeriesPoint[]): SeriesPoint[] => {
  const seen = new Set<number>()
  const out: SeriesPoint[] = []
  for (const p of pts) if (!seen.has(p.year)) { seen.add(p.year); out.push(p) }
  return out.sort((a, b) => a.year - b.year)
}

// Dense samples per segment for the monotone smoothing; matches the fidelity
// the chart's hand-rolled sampler used before it moved to graphUtils.
const SAMPLES_PER_SEGMENT = 20

const polyStr = (s: PathPoint[]) => s.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")

const valueAtX = (samples: PathPoint[], x: number): PathPoint => {
  for (let i = 1; i < samples.length; i++) {
    if (samples[i].x >= x) { const a = samples[i - 1], b = samples[i], f = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x); return { x, y: a.y + (b.y - a.y) * f } }
  }
  return samples[samples.length - 1]
}

// Slice a dense sample list to [xMin,xMax] with exact interpolated endpoints.
const sliceByX = (samples: PathPoint[], xMin: number, xMax: number): PathPoint[] => {
  const out: PathPoint[] = [valueAtX(samples, xMin)]
  for (const p of samples) if (p.x > xMin && p.x < xMax) out.push(p)
  out.push(valueAtX(samples, xMax))
  return out
}

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹"
const toSuperscript = (n: number) =>
  String(n).split("").map((c) => SUPERSCRIPT_DIGITS[Number(c)]).join("")

// 10¹³ and up: mantissa×10ˣ with real superscripts, one significant digit
// (two when it starts with 1). The unit symbol drops in this form — the axis
// states it. Returns null below the threshold.
const fmtPowersOfTen = (v: number): string | null => {
  if (v < 1e13) return null
  let e = Math.floor(Math.log10(v))
  let m = v / 10 ** e
  if (m >= 9.5) { m = 1; e += 1 }
  const mantissa = m < 1.95 ? m.toFixed(1).replace(/\.0$/, "") : String(Math.round(m))
  return (mantissa === "1" ? "" : mantissa + "×") + "10" + toSuperscript(e)
}

const withSuffix = (n: number, s: string) =>
  (n < 10 ? n.toFixed(1).replace(/\.0$/, "") : String(Math.round(n))) + s

// Letter suffixes K/M/T — no "B": next to the plan letter tags a B suffix
// would read as Plan B, so T takes over from 10⁹.
const fmtSuffixed = (v: number) => {
  if (v >= 1e9) return withSuffix(v / 1e9, "T")
  if (v >= 1e6) return withSuffix(v / 1e6, "M")
  if (v >= 1e3) return withSuffix(v / 1e3, "K")
  return String(Math.round(v))
}

// Format an auto-derived value label per the axis unit (defaults: log→currency,
// linear→percent). Used for Plan A's end label (unless a metric authors an
// endLabels override), the hover readout, and the header's live readout.
// No unit WORDS (yr/mo) — the axis labels carry those; $/%/×/K/M/T symbols
// stay, and 10¹³+ switches to the powers-of-ten form.
const fmtValue = (axis: MetricAxis, v: number) => {
  const unit = axis.unit ?? (axis.type === "log" ? "currency" : "percent")
  const powers = fmtPowersOfTen(v)
  switch (unit) {
    case "currency":
      return powers ? "$" + powers : "$" + fmtSuffixed(v)
    case "count":
    case "humanEquiv":
      return powers ?? fmtSuffixed(v)
    case "multiplier":
      // Lowercase k keeps the existing "158k×" style; M/T stay uppercase.
      return powers ?? fmtSuffixed(v).replace("K", "k") + "×"
    case "years":
      return v > 0 && v < 1 ? v.toFixed(1) : String(Math.round(v))
    case "percent":
    default: {
      const pct = v * 100
      return (pct < 10 ? pct.toFixed(1) : String(Math.round(pct))) + "%"
    }
  }
}

// A point list's RAW value at a (fractional) year. Interpolates the series
// in value space (log-space on log axes), independent of chart pixels; the
// year is clamped to the points' extent. Shared core for the two exported
// forms below (this branch's numeric readout + main's formatted one).
const pointsNumericValueAtYear = (pts: SeriesPoint[], axis: MetricAxis, year: number): number | null => {
  if (pts.length === 0) return null
  const clamped = Math.min(Math.max(year, pts[0].year), pts[pts.length - 1].year)
  let v = pts[pts.length - 1].value
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].year >= clamped) {
      const a = pts[i - 1], b = pts[i]
      const f = b.year === a.year ? 0 : (clamped - a.year) / (b.year - a.year)
      v = axis.type === "log"
        ? 10 ** (Math.log10(a.value) + (Math.log10(b.value) - Math.log10(a.value)) * f)
        : a.value + (b.value - a.value) * f
      break
    }
  }
  return v
}

// The metric's focal-line raw value at a (fractional) year. Exported for the
// panel redesigns, which derive the current capability level from the
// numeric speedup.
export const metricNumericValueAtYear = (series: MetricScenarioSeries, axis: MetricAxis, year: number): number | null =>
  pointsNumericValueAtYear(uniqByYear([...series.observed, ...series.projection, ...(series.branches.A ?? [])]), axis, year)

// A point list's value at a (fractional) year, formatted for display.
export const pointsValueAtYear = (pts: SeriesPoint[], axis: MetricAxis, year: number): string | null => {
  const v = pointsNumericValueAtYear(pts, axis, year)
  return v === null ? null : fmtValue(axis, v)
}

// The metric's focal-line value at a (fractional) year — drives the live
// readout in the details header while the reader is on Plan A's timeline.
export const metricValueAtYear = (series: MetricScenarioSeries, axis: MetricAxis, year: number): string | null =>
  pointsValueAtYear(uniqByYear([...series.observed, ...series.projection, ...(series.branches.A ?? [])]), axis, year)

const dateToFractionalYear = (d: Date) => {
  const y = d.getFullYear()
  const start = new Date(y, 0, 1).getTime()
  const end = new Date(y + 1, 0, 1).getTime()
  return y + (d.getTime() - start) / (end - start)
}

// A branch drawn against Plan A in the default path's red dashed line design
// — the plan letter in the hover readout is what tells them apart. `color`
// overrides the red for a single branch if a design ever needs it.
export type ComparisonBranch = { id: ComparisonPlanId; points: SeriesPoint[]; color?: string }

type MetricScenarioChartProps = {
  series: MetricScenarioSeries
  axis: MetricAxis
  // Optional per-branch override; defaults to fmtValue of the branch's last point.
  endLabels?: Partial<Record<BranchKey, string>>
  splitYear: number
  // Current HUD year (fractional) — the now-dot rides Plan A up to this point.
  year: number
  // The branches drawn against Plan A (must be referentially stable — the
  // geometry memo keys off it).
  comparisonBranches: ComparisonBranch[]
  // While the reader is on a non-A plan's timeline, the id of that plan's
  // comparison branch: the realized overlay and now-dot ride its line (to the
  // end of its data) instead of Plan A's.
  activeBranchId?: ComparisonPlanId | null
  // Narrower drawing space (viewBox width) for half-column placements: the
  // plot compresses horizontally while every label stays at its authored
  // font size (the container should be viewWidth px wide so scale stays ≈1).
  // Gutters keep their absolute widths; only the plot span shrinks.
  viewWidth?: number
  // Shaves this many px off the chart's bottom: the plot floor and the year
  // labels ride up together. For metrics whose axis floor hugs the lowest
  // line, the strip under the curves was pure whitespace.
  bottomCropPx?: number
}

export const MetricScenarioChart = memo(
  ({ series, axis, endLabels, splitYear, year, comparisonBranches, activeBranchId = null, viewWidth, bottomCropPx }: MetricScenarioChartProps) => {
    const vw = viewWidth ?? W
    // Plot right edge, preserving the stock right-gutter width (W - PX1).
    const px1 = vw - (W - PX1)
    // Cropped drawing height / plot bottom (see bottomCropPx).
    const h = H - (bottomCropPx ?? 0)
    const py1 = PY1 - (bottomCropPx ?? 0)
    // Hovered x in viewBox coordinates (null when the pointer is off the plot)
    // — drives the exact-value readout riding the lines.
    const [hoverX, setHoverX] = useState<number | null>(null)
    // Distinguishes a stationary second tap (toggle the readout off) from a
    // scrubbing drag on touch devices.
    const touchRef = useRef<{ moved: boolean; wasVisible: boolean }>({ moved: false, wasVisible: false })
    // The HUD year changes in discrete jumps (chart-date markers); the shared
    // HUD date tween glides the now-cursor between them.
    const targetDate = useMemo(
      () => new Date(Math.floor(year), Math.round((year % 1) * 12), 1),
      [year],
    )
    const animYear = dateToFractionalYear(useAnimatedDate(targetDate))

    // Everything that doesn't depend on the animated year or hover: scales,
    // smoothed samples, polyline strings, and label placement. Recomputing
    // this per animation frame is what the memo avoids. The year span is
    // data-derived — first observed point to the last branch point.
    const comparisons = comparisonBranches

    const geom = useMemo(() => {
      const yFor = yForValueWith(axis, py1)

      // Focal line = observed → projection → Plan A. Static styled segments
      // (thick observed, dotted shared projection, dashed Plan A); the
      // thin-solid realized overlay + now-cursor ride OVER them as `year` advances.
      const focalRaw = uniqByYear([...series.observed, ...series.projection, ...series.branches.A])
      const firstYear = focalRaw[0].year
      const lastYear = focalRaw[focalRaw.length - 1].year
      const lastAxisYear = Math.max(
        lastYear,
        ...comparisons.flatMap((c) => c.points.map((p) => p.year)),
      )
      const xForYear = (yr: number) => PX0 + ((yr - firstYear) / (lastAxisYear - firstYear)) * (px1 - PX0)
      const toXY = (p: SeriesPoint): PathPoint => ({ x: xForYear(p.year), y: yFor(p.value) })

      // Observed runs to its last real point; the realized overlay + fork start there.
      const observedEndYear = series.observed[series.observed.length - 1]?.year ?? firstYear
      const focal = createMonotoneSmoothedScreenPoints(focalRaw.map(toXY), SAMPLES_PER_SEGMENT)
      const xObsEnd = xForYear(observedEndYear)
      const xFork = xForYear(splitYear)
      const observedSeg = sliceByX(focal, xForYear(firstYear), xObsEnd)
      const sharedSeg = sliceByX(focal, xObsEnd, xFork)
      const planASeg = sliceByX(focal, xFork, xForYear(lastYear))
      // Comparison lines are smoothed WITH the shared trunk prepended and then
      // sliced back to the fork, so each branch leaves the dotted projection
      // tangentially instead of at a hard elbow. The two plot edges treat
      // off-axis values differently: below the floor clamps and keeps going
      // (income's $0 run draws along the bottom edge to its data end), but a
      // line that hits the CEILING stops at the exact crossing — a blow-up
      // (capability, labor) exits the top of the plot rather than crawling
      // flat along the ceiling, and its terminal dot marks the exit.
      const trunkTail = uniqByYear([...series.observed, ...series.projection]).filter((p) => p.year <= splitYear)
      const comparisonSamples = comparisons.map((c) => {
        if (c.points.length < 2) return { id: c.id, color: c.color ?? RED, samples: [] as PathPoint[] }
        const extended = uniqByYear([...trunkTail, ...c.points])
        const smoothed = createMonotoneSmoothedScreenPoints(extended.map(toXY), SAMPLES_PER_SEGMENT)
        const raw = sliceByX(smoothed, xFork, smoothed[smoothed.length - 1].x)
        const samples: PathPoint[] = []
        for (let i = 0; i < raw.length; i++) {
          const p = raw[i]
          if (p.y >= PY0) {
            samples.push(p.y > py1 ? { x: p.x, y: py1 } : p)
            continue
          }
          const prev = raw[i - 1]
          if (prev && prev.y > PY0) {
            const t = (PY0 - prev.y) / (p.y - prev.y)
            samples.push({ x: prev.x + t * (p.x - prev.x), y: PY0 })
          }
          break
        }
        return { id: c.id, color: c.color ?? RED, samples }
      })

      // End-of-line labelling: Plan A (the protagonist) prints its end VALUE
      // (authored endLabels.A override or derived); every comparison line
      // ends with a terminal dot — marking where that scenario's forecast
      // ends — and its plan LETTER, so which-line-is-which reads at a glance
      // without hovering. Exact values stay in the hover readout.
      const aPts = series.branches.A ?? []
      const aLast = aPts.length > 1 ? aPts[aPts.length - 1] : null
      const aEndLabel = aLast
        ? {
            label: endLabels?.A ?? fmtValue(axis, aLast.value),
            x: xForYear(aLast.year),
            y: yFor(aLast.value),
            labelY: yFor(aLast.value) + 3,
          }
        : null
      const endTags = comparisonSamples
        .filter((c) => c.samples.length > 1)
        .map((c) => {
          const p = c.samples[c.samples.length - 1]
          return { id: c.id, color: c.color, x: p.x, y: p.y, labelY: p.y + 3 }
        })
      // Push labels apart where lines end near each other (same y-cluster at
      // a similar x), and keep everything inside the plot's vertical span.
      const TAG_GAP = 10
      const stacked: Array<{ x: number; labelY: number }> = [...endTags, ...(aEndLabel ? [aEndLabel] : [])]
      stacked.sort((a, b) => a.labelY - b.labelY)
      for (let i = 1; i < stacked.length; i++) {
        const prev = stacked[i - 1]
        const cur = stacked[i]
        if (Math.abs(cur.x - prev.x) < 30 && cur.labelY - prev.labelY < TAG_GAP) {
          cur.labelY = prev.labelY + TAG_GAP
        }
      }
      for (const t of stacked) t.labelY = Math.min(py1 - 1, Math.max(PY0 + 6, t.labelY))

      return {
        endTags,
        yFor, xForYear, focal, firstYear, lastYear, observedEndYear,
        xObsEnd, xFork, comparisonSamples, aEndLabel,
        observedStr: polyStr(observedSeg),
        sharedStr: polyStr(sharedSeg),
        planAStr: planASeg.length > 1 ? polyStr(planASeg) : "",
        comparisonStrs: comparisonSamples.map((c) => ({
          id: c.id,
          color: c.color,
          str: c.samples.length > 1 ? polyStr(c.samples) : "",
        })),
      }
    }, [series, axis, endLabels, splitYear, comparisons, px1, py1])

    const { yFor, xForYear, focal, comparisonSamples } = geom
    const isLog = axis.type === "log"

    // The selected plan's drawn line (fork → its data end) when the reader is
    // off Plan A's timeline.
    const activeBranch = activeBranchId
      ? comparisonSamples.find((c) => c.id === activeBranchId && c.samples.length > 1) ?? null
      : null
    const now = Math.min(Math.max(animYear, geom.observedEndYear), geom.lastYear)
    // Riding a branch, the cursor tracks the HUD year but stops at the branch
    // line's end (its scenario ends before Plan A's). A branch was requested
    // but has no drawable line: hold at the fork rather than advancing the dot
    // up Plan A's line, which the reader is no longer on.
    const cursorX = activeBranch
      ? Math.min(xForYear(Math.max(animYear, geom.observedEndYear)), activeBranch.samples[activeBranch.samples.length - 1].x)
      : activeBranchId !== null
        ? geom.xFork
        : xForYear(now)
    const onBranchLine = activeBranch !== null && cursorX > geom.xFork
    const dot = onBranchLine ? valueAtX(activeBranch.samples, cursorX) : valueAtX(focal, cursorX)
    // Realized overlay: solid over the trunk up to the cursor — held at the
    // fork while riding a branch, whose own solid overlay continues from there.
    const realizedTrunkEndX = activeBranch ? Math.min(cursorX, geom.xFork) : cursorX
    const realizedSeg = realizedTrunkEndX > geom.xObsEnd ? sliceByX(focal, geom.xObsEnd, realizedTrunkEndX) : []
    const realizedBranchSeg = onBranchLine ? sliceByX(activeBranch.samples, geom.xFork, cursorX) : []

    // Value at a plot y — for the hover readout; inverting the drawn curve's y
    // keeps the readout dot exactly on the line. Clamped to the axis domain:
    // the smoothed samples carry float dust, and a value a hair below zero
    // would format as "-0.0".
    const valueForY = (py: number) => {
      const t = Math.min(1, Math.max(0, 1 - (py - PY0) / (py1 - PY0)))
      if (axis.type === "log") {
        const lo = Math.log10(axis.min)
        const hi = Math.log10(axis.max)
        return 10 ** (lo + t * (hi - lo))
      }
      return t * (axis.max ?? 1)
    }

    // Hover readout markers: the value pinned to each line under the pointer,
    // with the plan letter after it on the comparison lines — the letters
    // appear ONLY here, not at rest on the chart.
    const hoverMarkers = (() => {
      if (hoverX === null) return []
      const hx = hoverX
      const flip = hx > px1 - 52
      const markers: { key: string; color: string; point: PathPoint; labelY: number; label: string; flip: boolean }[] = []
      if (focal.length > 1 && hx >= focal[0].x && hx <= focal[focal.length - 1].x) {
        const point = valueAtX(focal, hx)
        markers.push({ key: "A", color: BLACK, point, labelY: point.y - 5, label: fmtValue(axis, valueForY(point.y)), flip })
      }
      for (const { id, color, samples } of comparisonSamples) {
        if (samples.length > 1 && hx >= samples[0].x && hx <= samples[samples.length - 1].x) {
          const point = valueAtX(samples, hx)
          markers.push({ key: id, color, point, labelY: point.y - 5, label: `${fmtValue(axis, valueForY(point.y))} ${id}`, flip })
        }
      }
      markers.sort((a, b) => a.labelY - b.labelY)
      const LABEL_GAP = 13
      for (let i = 1; i < markers.length; i++) {
        if (markers[i].labelY - markers[i - 1].labelY < LABEL_GAP) markers[i].labelY = markers[i - 1].labelY + LABEL_GAP
      }
      // Push the stack back up if it ran past the plot bottom, so no readout
      // ends up under the year labels or outside the chart.
      if (markers.length > 0 && markers[markers.length - 1].labelY > py1 - 2) {
        markers[markers.length - 1].labelY = py1 - 2
        for (let i = markers.length - 2; i >= 0; i--) {
          if (markers[i + 1].labelY - markers[i].labelY < LABEL_GAP) markers[i].labelY = markers[i + 1].labelY - LABEL_GAP
        }
      }
      return markers
    })()

    return (
      <svg
        viewBox={`0 0 ${vw} ${h}`}
        role="img"
        aria-label="So far (solid black); after the split, Plan A is the heavy black dashed line, the default path the red dashed line, and plans B, C, S faint gray dotted lines — each ends with a dot and its plan letter; hover shows exact values"
        // overflow-visible: hover value labels near the plot edges may extend a
        // few px past the viewBox — let them paint instead of cropping.
        className="block h-auto w-full overflow-visible font-mono"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          // Half-viewBox-px quantization: vertical pointer motion repeats the
          // same value and React bails out instead of re-rendering the SVG.
          const x = Math.round(((e.clientX - rect.left) / rect.width) * vw * 2) / 2
          setHoverX(x >= PX0 && x <= px1 ? x : null)
        }}
        onMouseLeave={() => setHoverX(null)}
        // Touch: a tap shows the readout (and a drag scrubs it); it stays
        // after the finger lifts, and a second stationary tap hides it again.
        onTouchStart={(e) => {
          touchRef.current = { moved: false, wasVisible: hoverX !== null }
          const rect = e.currentTarget.getBoundingClientRect()
          const x = Math.round(((e.touches[0].clientX - rect.left) / rect.width) * vw * 2) / 2
          setHoverX(x >= PX0 && x <= px1 ? x : null)
        }}
        onTouchMove={(e) => {
          touchRef.current.moved = true
          const rect = e.currentTarget.getBoundingClientRect()
          const x = Math.round(((e.touches[0].clientX - rect.left) / rect.width) * vw * 2) / 2
          setHoverX(x >= PX0 && x <= px1 ? x : null)
        }}
        onTouchEnd={() => {
          if (!touchRef.current.moved && touchRef.current.wasVisible) setHoverX(null)
        }}
      >
        {/* Y labels in the left gutter, flush with the column's left edge
            (start-anchored at x=0 — end-anchoring indented the short labels). */}
        {isLog
          ? axis.ticks.map((t) => (
              <text key={t.label} x={0} y={yFor(t.value) + 2.5} textAnchor="start" fontSize="8.5" fill={BLACK}>
                {t.label}
              </text>
            ))
          : (
            <>
              {axis.topLabel && (
                <text x={0} y={PY0 + 3} textAnchor="start" fontSize="8.5" fill={BLACK}>{axis.topLabel}</text>
              )}
              <text x={0} y={py1 + 2.5} textAnchor="start" fontSize="8.5" fill={BLACK}>0</text>
            </>
          )}

        {/* Scenario lines (strings prebuilt in the geom memo), bottom-up in
            emphasis order: faint dotted B/C/S context first, then the red
            default dash, then Plan A's heavy black dash on top. Each line's
            plan letter sits at its end (endTags below). */}
        {[...geom.comparisonStrs]
          .sort((a, b) => (a.id === "D" ? 1 : 0) - (b.id === "D" ? 1 : 0))
          .map(({ id, color, str }) =>
            str ? (
              <polyline
                key={id}
                points={str}
                fill="none"
                stroke={color}
                strokeWidth={id === "D" ? 1.5 : CONTEXT_STROKE_WIDTH}
                strokeDasharray={id === "D" ? DEFAULT_DASH : CONTEXT_DASH}
                strokeLinecap={id === "D" ? undefined : "round"}
              />
            ) : null,
          )}
        <polyline points={geom.sharedStr} fill="none" stroke={BLACK} strokeWidth={1.5} strokeDasharray="1 3.5" strokeLinecap="round" />
        {geom.planAStr && (
          <polyline points={geom.planAStr} fill="none" stroke={BLACK} strokeWidth={PLAN_A_STROKE_WIDTH} strokeDasharray={PLAN_A_DASH} />
        )}
        <polyline points={geom.observedStr} fill="none" stroke={BLACK} strokeWidth={2.5} />
        {realizedSeg.length > 1 && (
          <polyline points={polyStr(realizedSeg)} fill="none" stroke={BLACK} strokeWidth={PLAN_A_STROKE_WIDTH} />
        )}
        {realizedBranchSeg.length > 1 && (
          <polyline points={polyStr(realizedBranchSeg)} fill="none" stroke={activeBranch?.color ?? RED} strokeWidth={1.5} />
        )}

        {/* Now-dot riding the focal line — or the selected plan's branch line
            once past the fork (the header readout carries the value). */}
        <circle cx={dot.x} cy={dot.y} r={3.4} fill={onBranchLine ? activeBranch?.color ?? RED : BLACK} />

        {/* Hover readout: the exact value pinned to each line under the
            pointer. A line only reads out within its own data span — once a
            line has ended, no value (never a clamped/extrapolated one). Each
            label wears a page-background halo so it stays legible crossing
            the lines, and near-equal readouts get pushed apart vertically. */}
        {hoverMarkers.map(({ key, color, point, labelY, label, flip }) => (
          <g key={key}>
            <circle cx={point.x} cy={point.y} r={2.5} fill={color} />
            <text
              x={flip ? point.x - 7 : point.x + 7}
              y={labelY}
              textAnchor={flip ? "end" : "start"}
              fontSize="9"
              fontWeight="bold"
              fill={color}
              stroke="#fffff8"
              strokeWidth={5}
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {label}
            </text>
          </g>
        ))}

        {/* Three bare year anchors: data start, the fork, data end. */}
        <text x={PX0} y={h - 2} textAnchor="start" fontSize="8.5" fill={BLACK}>{geom.firstYear}</text>
        <text x={geom.xFork} y={h - 2} textAnchor="middle" fontSize="8.5" fill={BLACK}>{splitYear}</text>
        <text x={xForYear(geom.lastYear)} y={h - 2} textAnchor="end" fontSize="8.5" fill={BLACK}>{geom.lastYear}</text>

        {/* Resting identity: a terminal dot at each comparison line's end
            (where that scenario's forecast stops) with its plan letter in
            the line's own ink; Plan A ends with a dot and its printed VALUE
            instead. Paper halos keep everything legible over crossing
            lines. */}
        {geom.endTags.map((t) => (
          <g key={`end-${t.id}`}>
            <circle cx={t.x} cy={t.y} r={2} fill={t.color} />
            <text
              x={t.x + 4.5}
              y={t.labelY}
              textAnchor="start"
              fontSize="8.5"
              fontWeight="bold"
              fill={t.color}
              stroke="#fffff8"
              strokeWidth={3}
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {t.id}
            </text>
          </g>
        ))}
        {geom.aEndLabel && (
          <g>
            <circle cx={geom.aEndLabel.x} cy={geom.aEndLabel.y} r={2} fill={BLACK} />
            <text
              x={geom.aEndLabel.x + 5}
              y={geom.aEndLabel.labelY}
              textAnchor="start"
              fontSize="10"
              fontWeight="bold"
              fill={BLACK}
              stroke="#fffff8"
              strokeWidth={3}
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {geom.aEndLabel.label}
            </text>
          </g>
        )}

      </svg>
    )
  }
)
MetricScenarioChart.displayName = "MetricScenarioChart"
